import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, Spin, Tag, message } from 'antd';
import { ExportOutlined, SaveOutlined } from '@ant-design/icons';
import { get, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import type { AISettingType } from './types';
import './AIManagement.css';

const { TextArea } = Input;
const FIXED_BASE_URL = 'https://api.quickrouter.ai/v1';
const QUICKROUTER_SITE_URL = 'https://quickrouter.ai/';

const MODEL_OPTIONS = [
  { label: 'Claude Sonnet 4.6（海外推荐）', value: 'claude-sonnet-4-6' },
  { label: 'Claude Opus 4.6（高质量）', value: 'claude-opus-4-6' },
  { label: 'Claude Haiku 4.5（轻量）', value: 'claude-haiku-4-5' },
  { label: 'DeepSeek V3.1（中文推荐）', value: 'deepseek-v3-1-250821' },
  { label: 'DeepSeek Chat', value: 'deepseek-chat' },
  { label: 'GPT-5.5', value: 'gpt-5.5' },
  { label: 'GPT-4.1', value: 'gpt-4.1' },
  { label: 'GPT-4.1 Mini', value: 'gpt-4.1-mini' },
  { label: 'GPT-4o', value: 'gpt-4o' },
  { label: 'GPT-4o Mini', value: 'gpt-4o-mini' },
];

const IMAGE_MODEL_OPTIONS = [
  { label: '可灵 Kling v1（便宜首选，推荐）', value: 'kling-v1' },
  { label: '可灵 Kling v1.5（便宜，画面更稳）', value: 'kling-v1-5' },
  { label: '可灵 Kling v2.1（质量兜底）', value: 'kling-v2-1' },
  { label: 'Gemini 2.5 Flash Image（快速兜底）', value: 'gemini-2.5-flash-image' },
  { label: 'Gemini 3 Pro Image Preview（高质量兜底）', value: 'gemini-3-pro-image-preview' },
  { label: 'GPT Image 1（昂贵，最后兜底）', value: 'gpt-image-1' },
  { label: 'GPT Image 2（昂贵，高质量）', value: 'gpt-image-2' },
  { label: 'DALL·E 3（昂贵，兼容兜底）', value: 'dall-e-3' },
];

const IMAGE_SIZE_OPTIONS = [
  { label: '1024 × 1536（竖版封面）', value: '1024x1536' },
  { label: '1024 × 1024（方图测试）', value: '1024x1024' },
  { label: '768 × 1024（轻量竖图）', value: '768x1024' },
];

const AIConfigPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<AISettingType | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<AISettingType>(API_ENDPOINTS.ai_management.config);
      setConfig(data);
      form.setFieldsValue({
        base_url: data.base_url,
        model: data.model,
        image_model: data.image_model,
        image_fallback_model: data.image_fallback_model,
        image_size: data.image_size,
        api_key: '',
        system_prompt: data.system_prompt,
        temperature: data.temperature,
        max_tokens: data.max_tokens,
        ink_multiplier: data.ink_multiplier,
      });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载AI配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await put(API_ENDPOINTS.ai_management.config, {
        model: values.model,
        image_model: values.image_model,
        image_fallback_model: values.image_fallback_model,
        image_size: values.image_size,
        api_key: values.api_key,
        system_prompt: values.system_prompt,
        temperature: values.temperature,
        max_tokens: values.max_tokens,
        ink_multiplier: values.ink_multiplier,
      });
      message.success('AI配置已保存');
      await loadConfig();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ai-management-page">
      <h1 className="page-title">AI配置</h1>

      <Card bordered={false} className="ai-section-card">
        <Spin spinning={loading}>
          {config && (
            <Space wrap className="ai-summary">
              <Tag color="blue">
                提供商: {config.provider}
                <a className="ai-provider-link" href={QUICKROUTER_SITE_URL} target="_blank" rel="noreferrer">
                  官网/充值 <ExportOutlined />
                </a>
              </Tag>
              <Tag color="geekblue">写作模型: {config.model}</Tag>
              <Tag color="cyan">生图首选: {config.image_model}</Tag>
              <Tag color="gold">生图兜底: {config.image_fallback_model}</Tag>
              <Tag color="lime">墨水倍率: {config.ink_multiplier || 2}x</Tag>
              <Tag color={config.has_api_key ? 'green' : 'red'}>
                Key: {config.has_api_key ? '已配置' : '未配置'}
              </Tag>
              <Tag color="purple">Base URL: {config.base_url}</Tag>
            </Space>
          )}

          <Alert
            type="info"
            showIcon
            message="保存后，测试页和Web 前台AI创作都会真实使用这里的模型配置。写作模型用于小说文本，生图模型用于封面生成；首选模型失败时会自动尝试兜底模型。Key 不回显明文，留空时会保留当前值。"
            description="生图默认按便宜优先处理：可灵首选，Gemini 兜底，GPT Image 仅作为最后昂贵兜底。"
            style={{ marginBottom: 16 }}
          />

          <Form form={form} layout="vertical">
            <Form.Item
              label="Base URL"
              name="base_url"
            >
              <Input placeholder={FIXED_BASE_URL} disabled />
            </Form.Item>

            <Form.Item
              label="模型"
              name="model"
              rules={[{ required: true, message: '请输入模型名称' }]}
            >
              <Select options={MODEL_OPTIONS} placeholder="请选择模型" />
            </Form.Item>

            <div className="ai-form-grid">
              <Form.Item
                label="首选生图模型"
                name="image_model"
                rules={[{ required: true, message: '请选择生图模型' }]}
                extra="用于发布页 AI 生成封面，也用于 AI 生图测试。推荐可灵：便宜、适合封面；失败时后端还会继续尝试兜底模型池。"
              >
                <Select options={IMAGE_MODEL_OPTIONS} placeholder="请选择生图模型" />
              </Form.Item>

              <Form.Item
                label="兜底生图模型"
                name="image_fallback_model"
                rules={[{ required: true, message: '请选择兜底生图模型' }]}
                extra="首选模型限流、失败或没有返回图片时，会自动切到这里。建议 Gemini 2.5 Flash Image，速度快且成本低于 GPT 生图。"
              >
                <Select options={IMAGE_MODEL_OPTIONS} placeholder="请选择兜底生图模型" />
              </Form.Item>
            </div>

            <div className="ai-form-grid">
              <Form.Item
                label="默认生图尺寸"
                name="image_size"
                rules={[{ required: true, message: '请选择默认生图尺寸' }]}
              >
                <Select options={IMAGE_SIZE_OPTIONS} placeholder="请选择默认尺寸" />
              </Form.Item>
            </div>

            <Form.Item
              label={(
                <Space size={8}>
                  <span>API Key</span>
                  <Tag color={config?.has_api_key ? 'green' : 'red'}>
                    {config?.has_api_key ? '已保存' : '未保存'}
                  </Tag>
                </Space>
              )}
              name="api_key"
              extra={config?.has_api_key ? '当前 Key 已保存，输入新 Key 后会覆盖旧值。' : '请输入 QuickRouter API Key。'}
            >
              <Input.Password placeholder={config?.has_api_key ? '已保存，留空不修改' : '请输入 API Key'} />
            </Form.Item>

            <Form.Item label="系统提示词" name="system_prompt">
              <TextArea rows={5} placeholder="请输入系统提示词" />
            </Form.Item>

            <div className="ai-form-grid">
              <Form.Item
                label="温度"
                name="temperature"
                rules={[{ required: true, message: '请输入温度' }]}
              >
                <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="最大输出"
                name="max_tokens"
                rules={[{ required: true, message: '请输入最大输出' }]}
              >
                <InputNumber min={1} max={4096} step={1} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="墨水计费倍率"
                name="ink_multiplier"
                rules={[{ required: true, message: '请输入墨水计费倍率' }]}
                extra="AI调用按总 tokens 乘以该倍率扣除墨水；最低 1 倍。"
              >
                <InputNumber min={1} max={20} step={0.1} precision={1} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </Form>
        </Spin>
      </Card>

      <div className="ai-actions">
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          保存配置
        </Button>
      </div>
    </div>
  );
};

export default AIConfigPage;
